import React, { useState, useEffect, useCallback } from "react";
import {
  Loader,
  Message,
  Button,
  Icon,
  Popup,
  Confirm,
  Modal,
  List,
  Image,
} from "semantic-ui-react";
import {
  Modal as AntModal,
  Button as AntBtn,
  Popconfirm,
  Tooltip as AntTooltip,
  Empty,
  Spin,
  Alert,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DidacticRoutesAPI } from "../../../../api";
import { RutasDidacticasEditWizard } from "./RutasDidacticasEditWizard";
import { getShareScopeLabel } from "../../../../Utils/shareScope";
import { NEW_SALAS } from "../../../../ui/featureFlags";
import sx from "./RutasDidacticasList.module.scss";
import "../../../../Utils/shareScope.scss";
import "./RutasDidacticasList.scss";

const ITIN_IMG =
  "https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg";

const didacticRoutesAPI = new DidacticRoutesAPI();

export function RutasDidacticasList({
  teacher,
  teacherId,
  accessToken,
  reload,
}) {
  const { t } = useTranslation("itineraries");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Confirm de borrado (ruta completa)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);

  // Modal de edición de la ruta
  const [showEditModal, setShowEditModal] = useState(false);
  const [routeToEdit, setRouteToEdit] = useState(null);

  // Modal de “ver escenas”
  const [showScenesModal, setShowScenesModal] = useState(false);
  const [routeScenes, setRouteScenes] = useState([]); // Escenas de la ruta
  const [routeViewing, setRouteViewing] = useState(null); // Ruta que se está visualizando

  // **Nuevo** modal para mostrar la descripción de la ruta
  const [showRouteDescriptionModal, setShowRouteDescriptionModal] =
    useState(false);

  // ================ 1) Obtener listado de rutas ================
  const fetchRoutes = useCallback(async () => {
    if (!teacherId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await didacticRoutesAPI.getRoutesByTeacher(
        accessToken,
        teacherId,
      );
      setRoutes(result.routes || []);
      //console.log("Rutas didácticas:", routes);
    } catch (err) {
      console.error("Error fetching teacher routes:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, teacherId]);

  // Al montar el componente
  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Si hay un "reload" externo, volvemos a pedir rutas
  useEffect(() => {
    fetchRoutes();
  }, [reload, fetchRoutes]);

  // ================ 2) Ver escenas de la ruta (eye) ================
  const handleSeeClick = async (route) => {
    try {
      setLoading(true);
      setRouteViewing(route);

      let allScenes = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const result = await didacticRoutesAPI.getScenesByRoute(
          accessToken,
          route._id,
          currentPage,
          100,
        );
        allScenes.push(...(result.scenes || []));
        totalPages = result.totalPages;
        currentPage++;
      } while (currentPage <= totalPages);

      setRouteScenes(allScenes);
      setShowScenesModal(true);
    } catch (error) {
      console.error("Error al obtener las escenas del itinerario:", error);
    } finally {
      setLoading(false);
    }
  };

  // ================ 3) Borrar ruta completa ================
  const handleDeleteClick = (route) => {
    setRouteToDelete(route);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!routeToDelete) return;
    try {
      await didacticRoutesAPI.deleteRoute(accessToken, routeToDelete._id);
      // Re-fetchear tras borrar la ruta
      fetchRoutes();
    } catch (error) {
      console.error("Error al eliminar la ruta:", error);
    } finally {
      setShowDeleteConfirm(false);
      setRouteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRouteToDelete(null);
  };

  // ================ 4) Editar ruta (pencil) ================
  const handleEditClick = (route) => {
    setRouteToEdit(route);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setRouteToEdit(null);
  };

  // ================ 5) Borrar escena de la ruta (trash en cada escena) ================
  const handleDeleteSceneFromRoute = async (scene) => {
    if (!routeViewing || !scene) return;
    try {
      setLoading(true);
      await didacticRoutesAPI.deleteSceneFromRoute(
        accessToken,
        routeViewing._id,
        scene._id,
      );
      // Removemos la escena del array local, sin recargar
      setRouteScenes((prevScenes) =>
        prevScenes.filter((sc) => sc._id !== scene._id),
      );
    } catch (error) {
      console.error("Error al eliminar la escena de la ruta:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteRouteNow = async (route) => {
    try {
      await didacticRoutesAPI.deleteRoute(accessToken, route._id);
      fetchRoutes();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error al eliminar la ruta:", err);
    }
  };

  // ================ Render nuevo (AntD, NEW_SALAS) ================
  if (NEW_SALAS) {
    return (
      <div className={sx.panel}>
        <div className={sx.header}>
          <h2 className={sx.title}>{t("list.myItineraries")}</h2>
          <p className={sx.subtitle}>{t("list.myItinerariesSubtitle")}</p>
        </div>

        {loading ? (
          <div className={sx.center}>
            <Spin />
          </div>
        ) : error ? (
          <Alert
            type="error"
            showIcon
            message={t("list.errorTitle")}
            description={error}
          />
        ) : routes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("list.noItineraries")}
          />
        ) : (
          <div className={sx.list}>
            {routes.map((route) => (
              <div key={route._id} className={sx.card}>
                <div className={sx.info}>
                  <h3 className={sx.routeName}>{route.nameRute}</h3>
                  <div className={sx.meta}>
                    <span className={sx.scopeTag}>
                      <span className={sx.scopeDot} />
                      {getShareScopeLabel(route)}
                    </span>
                    <span className={sx.class}>
                      {route.classnumber || t("list.noClass")}
                    </span>
                  </div>
                  <div className={sx.actions}>
                    <AntTooltip title={t("list.reviewPopup")}>
                      <AntBtn
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleSeeClick(route)}
                      />
                    </AntTooltip>
                    <AntTooltip title={t("list.editPopup")}>
                      <AntBtn
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditClick(route)}
                      />
                    </AntTooltip>
                    <Popconfirm
                      title={t("list.confirmDelete")}
                      description={t("list.confirmDeleteContent", {
                        name: route.nameRute,
                      })}
                      okText={t("list.confirmDelete")}
                      cancelText={t("list.confirmNo")}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteRouteNow(route)}
                    >
                      <AntTooltip title={t("list.deletePopup")}>
                        <AntBtn
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </AntTooltip>
                    </Popconfirm>
                  </div>
                </div>
                <div className={sx.avatar}>
                  <img src={ITIN_IMG} alt="" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Wizard de edicion (se migra en el tramo del wizard) */}
        {showEditModal && routeToEdit && (
          <RutasDidacticasEditWizard
            open={showEditModal}
            teacher={teacher}
            onClose={handleCloseEditModal}
            route={routeToEdit}
            accessToken={accessToken}
            onRefresh={fetchRoutes}
          />
        )}

        {/* Modal: escenas del itinerario */}
        <AntModal
          open={showScenesModal}
          onCancel={() => setShowScenesModal(false)}
          width={620}
          destroyOnHidden
          title={`${t("list.scenesModalTitle")}: ${routeViewing?.nameRute || ""}`}
          footer={[
            <AntBtn
              key="desc"
              icon={<InfoCircleOutlined />}
              onClick={() => setShowRouteDescriptionModal(true)}
            >
              {t("list.routeDescTitle")}
            </AntBtn>,
            <AntBtn key="close" onClick={() => setShowScenesModal(false)}>
              {t("list.closeButton")}
            </AntBtn>,
          ]}
        >
          {loading ? (
            <div className={sx.center}>
              <Spin />
            </div>
          ) : routeScenes.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("list.noScenes")}
            />
          ) : (
            <div>
              {routeScenes.map((sc) => (
                <div key={sc._id} className={sx.sceneRow}>
                  <img className={sx.sceneThumb} src={sc.bannertopUrl} alt="" />
                  <div className={sx.sceneBody}>
                    <p className={sx.sceneTitle}>{sc.title}</p>
                    <p className={sx.sceneMeta}>
                      {t("list.categoryLabel")}: {sc.category.join(", ")}
                    </p>
                    <p className={sx.sceneMeta}>
                      {t("list.subcategoryLabel")}: {sc.subcategory.join(", ")}
                    </p>
                  </div>
                  <AntTooltip title={t("list.deletePopup")}>
                    <AntBtn
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSceneFromRoute(sc)}
                    />
                  </AntTooltip>
                </div>
              ))}
            </div>
          )}
        </AntModal>

        {/* Modal: descripcion del itinerario */}
        <AntModal
          open={showRouteDescriptionModal}
          onCancel={() => setShowRouteDescriptionModal(false)}
          width={520}
          destroyOnHidden
          title={t("list.routeDescTitle")}
          footer={[
            <AntBtn
              key="close"
              onClick={() => setShowRouteDescriptionModal(false)}
            >
              {t("list.closeButton")}
            </AntBtn>,
          ]}
        >
          <p>{routeViewing?.description || t("list.noDescription")}</p>
        </AntModal>
      </div>
    );
  }

  // ================ Render principal (legacy) ================
  if (loading) {
    return (
      <Loader active inline="centered">
        {t("list.loadingRoutes")}
      </Loader>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>{t("list.errorTitle")}</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  return (
    <div className="ui-materials">
      {/* Encabezado de Rutas */}
      <div className="intro-sections">
        <div className="intro-section rutas-creadas">
          <h2 className="intro-title">{t("list.myItineraries")}</h2>
          <p className="intro-description">{t("list.myItinerariesSubtitle")}</p>
          <hr className="intro-divider" />
        </div>
      </div>

      {/* Lista de Rutas o mensaje si no hay */}
      {routes.length === 0 ? (
        <Message info>
          <Message.Header>{t("list.noItineraries")}</Message.Header>
          <p>{t("list.noItinerariesContent")}</p>
        </Message>
      ) : (
        <div className="rute-list">
          {routes.map((route) => (
            <div key={route._id} className="e-card-rute">
              <div className="rute-info">
                <h3
                  className="rute-title"
                  //style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  {route.nameRute}
                </h3>
                <p className="rute-assigned">
                  <span className="badge-share">
                    {getShareScopeLabel(route)}
                  </span>
                  {"  "}
                  {route.classnumber || t("list.noClass")}
                </p>
                <div className="rute-buttons">
                  <Popup
                    content={t("list.reviewPopup")}
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleSeeClick(route)}
                      >
                        <Icon name="eye" />
                      </button>
                    }
                  />
                  <Popup
                    content={t("list.editPopup")}
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleEditClick(route)}
                      >
                        <Icon name="pencil" />
                      </button>
                    }
                  />
                  <Popup
                    content={t("list.deletePopup")}
                    position="top center"
                    inverted
                    trigger={
                      <button
                        className="e-button-info"
                        onClick={() => handleDeleteClick(route)}
                      >
                        <Icon name="trash" />
                      </button>
                    }
                  />
                </div>
              </div>
              <div className="rute-image">
                <img
                  src="https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg"
                  alt="Rute"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm para eliminar ruta completa */}
      <Confirm
        open={showDeleteConfirm}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        content={
          routeToDelete
            ? t("list.confirmDeleteContent", { name: routeToDelete.nameRute })
            : t("list.confirmDeleteGeneric")
        }
        cancelButton={t("list.confirmNo")}
        confirmButton={t("list.confirmDelete")}
      />

      {/* Modal de edición de ruta */}
      {showEditModal && routeToEdit && (
        <RutasDidacticasEditWizard
          open={showEditModal}
          teacher={teacher}
          onClose={handleCloseEditModal}
          route={routeToEdit}
          accessToken={accessToken}
          onRefresh={fetchRoutes}
        />
      )}

      {/* Modal de escenas de la ruta */}
      <Modal
        open={showScenesModal}
        onClose={() => setShowScenesModal(false)}
        size="tiny"
      >
        {/* Encabezado del modal de escenas */}
        <Modal.Header
          style={{ display: "flex", alignItems: "center", gap: "1rem" }}
        >
          {/* Botón “eye” para ver la descripción de la ruta */}
          <Button
            icon="info"
            color="blue"
            onClick={() => setShowRouteDescriptionModal(true)}
          />
          {t("list.scenesModalTitle")}: {routeViewing?.nameRute}
        </Modal.Header>

        <Modal.Content>
          {loading ? (
            <Loader active inline="centered" />
          ) : routeScenes.length === 0 ? (
            <Message info>{t("list.noScenes")}</Message>
          ) : (
            <List divided relaxed>
              {routeScenes.map((sc) => (
                <List.Item key={sc._id}>
                  <Image
                    src={sc.bannertopUrl}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: "1rem",
                    }}
                  />

                  <List.Content>
                    <List.Header>{sc.title}</List.Header>
                    <List.Description>
                      {t("list.categoryLabel")}: {sc.category.join(", ")} <br />
                      {t("list.subcategoryLabel")}: {sc.subcategory.join(", ")}
                    </List.Description>
                  </List.Content>

                  {/* Botón para borrar esta escena de la ruta */}
                  <List.Content floated="right">
                    <Button
                      icon="trash"
                      color="red"
                      onClick={() => handleDeleteSceneFromRoute(sc)}
                    />
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowScenesModal(false)} color="grey">
            {t("list.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Nuevo modal para la descripción de la ruta */}
      <Modal
        open={showRouteDescriptionModal}
        onClose={() => setShowRouteDescriptionModal(false)}
        size="tiny"
      >
        <Modal.Header>{t("list.routeDescTitle")}</Modal.Header>
        <Modal.Content>
          <p>{routeViewing?.description || t("list.noDescription")}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowRouteDescriptionModal(false)}
            color="grey"
          >
            {t("list.closeButton")}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
